import React, { useState, useCallback, useEffect } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MyButton } from '../../common/components';
import { MyStepperHeader } from '../../common/components/MyStepper';
import {
  registerUserValidationSchema,
} from '../../auth/RegisterUser/RegisterUserForm';
import {
  createClinicValidationSchema,
  initialValues as createClinicInitialValues,
} from './CreateClinic/CreateClinicForm';
import {
  createProviderValidationSchema,
} from './CreateProvider/CreateProviderForm';
import { gridSpacing } from '../../../common/themes/constants';
import Swal from 'sweetalert2';
import { isEmpty, omit } from 'lodash';
import PageContent from '../../common/MainLayout/Page';
import BackendErrorHandler from '../../common/components/hook-form/BackendErrorHandler';
import Error from '../../common/Error';
import clientGraphql from '../../../common/apollo-graphql';
import { GET_PERMISSIONS_QUERY, GET_NOTIFICATIONS_QUERY, GET_CLINICS_BY_NAME } from '../gql/Query';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { SAVE_CLINIC_MUTATION, SAVE_USER_CLINIC_MUTATION } from '../gql/Mutation';

import EnrollPsrReviewInfo from './EnrollPsrReviewInfo';
import ClinicStep from './EnrollClinic/ClinicStep';
import ProviderStep, { DEFAULT_PROVIDER_NAME } from './EnrollClinic/ProviderStep';
import UserStep from './EnrollClinic/UserStep';

const validationSchema = yup.object().shape({
  clinic: createClinicValidationSchema,
  providers: yup.array().of(createProviderValidationSchema),
  users: yup.array().of(registerUserValidationSchema),
});

const initialValues = {
  clinic: {
    ...createClinicInitialValues,
  },
  providers: [],
  users: [],
};

const stepsIndex = {
  clinic: 0,
  provider: 1,
  user: 2,
  review: 3,
};

const steps = [
  {
    label: 'Add Clinic',
  },
  {
    label: 'Add Providers',
  },
  {
    label: 'Add Users',
  },
  {
    label: 'Review',
  },
];

export default function EnrollClinic({ history }) {
  const [activeStep, setActiveStep] = useState(stepsIndex.clinic);
  const [existingUsers, setExistingUsers] = useState({ rows: [], count: 0 });
  const [parsedNotificationData, setParsedNotificationData] = useState({});

  const [addUserFromOtherClinicDialogVisible, setAddUserFromOtherClinicDialogVisible] = useState(
    false,
  );

  const [checkClinicName] = useLazyQuery(GET_CLINICS_BY_NAME, {
    client: clientGraphql,
    fetchPolicy: 'network-only',
  });

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid, dirtyFields },
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    shouldFocusError: true,
    mode: 'all',
    reValidateMode: 'onSubmit',
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'providers',
  });

  const { fields: fieldsUser, append: appendUser, remove: removeUser } = useFieldArray({
    control,
    name: 'users',
  });

  const handleNext = async () => {
    if (activeStep === stepsIndex.clinic) {
      const clinicName = getValues('clinic.name');
      if (!clinicName) return;

      try {
        const { data } = await checkClinicName({ variables: { name: clinicName } });

        if (data?.clinics?.length > 0) {
          setError('clinic.name', {
            type: 'manual',
            message: 'A clinic with this name already exists. Please use a different name.',
          });
          return;
        }
      } catch (err) {
        console.error('Error checking clinic name:', err);
      }
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };


  const handleBack = () => setActiveStep(prevActiveStep => prevActiveStep - 1);

  const handleOnEditUser = useCallback(() => {
    setActiveStep(stepsIndex.user);
  }, []);

  const handleOnEditClinic = useCallback(() => {
    setActiveStep(stepsIndex.clinic);
  }, []);

  const handleOnEditProvider = useCallback(() => {
    setActiveStep(stepsIndex.provider);
  }, []);

  const { data } = useQuery(GET_PERMISSIONS_QUERY, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: dataNotificationSettings } = useQuery(GET_NOTIFICATIONS_QUERY, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const [
    save_clinic,
    { data: saveClinicData, loading: saveClinicLoading, error: saveClinicError },
  ] = useMutation(SAVE_CLINIC_MUTATION, {
    client: clientGraphql,
  });

  const [
    save_user_clinic
  ] = useMutation(SAVE_USER_CLINIC_MUTATION, {
    client: clientGraphql,
  });

  const hasEmptyUserField = useCallback(
    (_dirty, _values) => {
      var users = getValues('users');
      var hasEmpty = false;
      users.forEach(user => {
        if (
          user.role_id === -1 ||
          isEmpty(user.email) ||
          isEmpty(user.firstname) ||
          isEmpty(user.lastname)
        ) {
          hasEmpty = true;
        }
      });
      return activeStep === stepsIndex.user && hasEmpty;
    },
    [getValues, activeStep],
  );

  const hasEmptyProviderField = useCallback(
    (_dirty, _values) => {
      var providers = getValues('providers');
      var hasEmpty = false;
      providers.forEach(provider => {
        if (
          provider.firstname !== DEFAULT_PROVIDER_NAME &&
          (
            isEmpty(provider.title) ||
            isEmpty(provider.firstname) ||
            isEmpty(provider.lastname) ||
            (provider.can_receive && isEmpty(provider.specializations))
          )
        ) {
          hasEmpty = true;
        }
      });
      return activeStep === stepsIndex.provider && hasEmpty;
    },
    [getValues, activeStep],
  );

  const hasEmptyClinicField = useCallback(
    (_dirty, _values) => {
      return (
        (activeStep === stepsIndex.clinic &&
          (isEmpty(getValues('clinic.name')) ||
            isEmpty(getValues('clinic.address_line1')) ||
            isEmpty(getValues('clinic.address_city')) ||
            isEmpty(getValues('clinic.address_state')))) ||
        isEmpty(getValues('clinic.address_zip'))
      );
    },
    [getValues, activeStep],
  );

  const onSubmit = data => {
    var newData = {
      clinic: JSON.parse(JSON.stringify(data.clinic)),
    };
    var newUsers = data.users.filter(user => { return !('id' in user); });
    var oldUsers = data.users.filter(user => { return ('id' in user); });
    console.log(oldUsers);
    if (isEmpty(newUsers)) {
      newUsers = [];
    }
    if (isEmpty(oldUsers)) {
      oldUsers = [];
    }
    newData.clinic['users'] = JSON.parse(JSON.stringify(newUsers));

    newUsers.forEach((user, index) => {
      newData.clinic.users[index]['provider_ids'] = [];
    });
    newData.clinic['providers'] = JSON.parse(JSON.stringify(data.providers));
    newData.clinic['extra_info'] = JSON.stringify({
      office_phone: newData.clinic['office_phone'],
      fax: newData.clinic['fax'],
      ...(newData.clinic['time_format']
        ? { time_format: newData.clinic['time_format'] }
        : {}),
    });
    data.providers.forEach((provider, index) => {
      if (
        provider.optional_documents.length === 0 ||
        provider.optional_documents.filter(e => e.document === 'misc').length <= 0
      ) {
        newData.clinic['providers'][index]['optional_documents'].push({
          document: 'misc',
          question: '',
        });
      }
    });

    newData.clinic = omit(newData.clinic, ['office_phone', 'fax', 'time_format']);

    save_clinic({
      client: clientGraphql,
      variables: newData,
    }).then(res => {
      console.log(res.data.save_clinic, oldUsers);
      oldUsers.forEach(user => {
        var existingUser = {};
        existingUser['userId'] = user.id;
        existingUser['clinicId'] = parseInt(res.data.save_clinic.id);
        existingUser['permission'] = user.permission;
        existingUser['notification'] = user.notification;
        save_user_clinic({
          client: clientGraphql,
          variables: { ...existingUser },
        });
      });
    });
  };

  const onError = (formErrors) => {
    const FIELD_LABELS = {
      providers: 'Provider',
      specializations: 'Specializations',
      users: 'User',
      phone: 'Phone'
    };

    const extractMessages = (errors, parentKey = '') => {
      let messages = [];

      if (typeof errors !== 'object' || errors === null) return messages;

      Object.entries(errors).forEach(([key, value]) => {
        let fullKey = parentKey ? (Number.isInteger(+key) ? `${parentKey}[${key}]` : `${parentKey}.${key}`) : key;

        if (value?.message) {
          const label = getFriendlyLabel(fullKey);
          messages.push(`${label}: ${value.message}`);
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            messages.push(...extractMessages(item, `${fullKey}[${index}]`));
          });
        } else if (typeof value === 'object') {
          messages.push(...extractMessages(value, fullKey));
        }
      });

      return messages;
    };

    const getFriendlyLabel = (path) => {
      return path
        .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i, 10) + 1}`)
        .split('.')
        .map((part) => {
          const [name, num] = part.split(' ');
          const label = FIELD_LABELS[name];
          return label ? label + (num ? ' ' + num : '') : part;
        })
        .join(' - ');
    };

    const allMessages = extractMessages(formErrors);

    Swal.fire({
      icon: 'error',
      title: 'Validation Error',
      html: `<ul style="text-align: left; padding-left: 1.2em;">${allMessages
        .map((msg) => `<li>${msg}</li>`)
        .join('')}</ul>`,
    });
  };

  useEffect(() => {
    if (!isEmpty(saveClinicError)) {
      const errors = !isEmpty(saveClinicError.graphQLErrors)
        ? saveClinicError.graphQLErrors[0]
        : {};
      if (!isEmpty(errors.user)) {
        handleOnEditUser();
      } else if (!isEmpty(errors.clinic)) {
        handleOnEditClinic();
      } else if (!isEmpty(errors.provider)) {
        handleOnEditProvider();
      } else if (
        !isEmpty(errors.extensions) &&
        !isEmpty(errors.extensions.validation) &&
        JSON.stringify(errors.extensions.validation).includes('clinic.users')
      ) {
        Swal.fire({
          icon: 'error',
          text: JSON.stringify(errors.extensions.validation),
          showConfirmButton: true,
        }).then(() => {
          handleOnEditUser();
        });
      } else if (
        !isEmpty(errors.extensions) &&
        !isEmpty(errors.extensions.validation) &&
        JSON.stringify(errors.extensions.validation).includes('clinic.provider')
      ) {
        Swal.fire({
          icon: 'error',
          text: JSON.stringify(errors.extensions.validation),
          showConfirmButton: true,
        }).then(() => {
          handleOnEditProvider();
        });
      } else if (
        !isEmpty(errors.extensions) &&
        !isEmpty(errors.extensions.validation) &&
        JSON.stringify(errors.extensions.validation).includes('clinic.')
      ) {
        Swal.fire({
          icon: 'error',
          text: JSON.stringify(errors.extensions.validation),
          showConfirmButton: true,
        }).then(() => {
          handleOnEditClinic();
        });
      } else {
        Swal.fire({
          icon: 'error',
          text: 'Error Message: ' + saveClinicError.message,
          showConfirmButton: true,
        });
      }
    }
    if (!isEmpty(saveClinicData)) {
      Swal.fire({
        icon: 'success',
        text: 'Successfully created a clinic',
        showConfirmButton: true,
      }).then(() => {
        history.push('/manage/clinics');
      });
    }
  }, [
    handleOnEditUser,
    handleOnEditClinic,
    handleOnEditProvider,
    saveClinicError,
    history,
    saveClinicData,
  ]);

  const handleRetrieve = useCallback(
    feature => {
      setValue('clinic.address_line1', feature.address_line1);
      setValue('clinic.address_city', feature.place);
      setValue('clinic.address_state', feature.region);
      setValue('clinic.address_zip', feature.postcode);
      setValue('clinic.full_address', feature.full_address);
      trigger('clinic.full_address');
    },
    [setValue, trigger],
  );

  useEffect(() => {
    if (!isEmpty(dataNotificationSettings)) {
      const newData = dataNotificationSettings.user_notification_settings.map(notification => {
        return { value: true, label: notification, name: notification };
      });
      setParsedNotificationData(newData);
    }
  }, [dataNotificationSettings, setParsedNotificationData]);

  const showReviewButton = activeStep === stepsIndex.user;

  return (
    <PageContent
      contentClass="clinics-enroll-psr"
      title="Register Clinic and Main User"
      contentSX={{ background: 'transparent', px: 4, py: 4 }}
    >
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Box sx={{ px: 6, mb: 6 }}>
          <MyStepperHeader activeStep={activeStep} steps={steps} />
        </Box>

        <BackendErrorHandler
          error={
            !isEmpty(saveClinicError) && !isEmpty(saveClinicError.graphQLErrors)
              ? saveClinicError.graphQLErrors[0]
              : null
          }
          setError={setError}
          clearErrors={clearErrors}
        />

        <Stack direction="column" spacing={4} sx={{ px: 4, pt: 4, pb: 6 }}>
          <Grid
            container
            justifyContent="center"
            alignItems="flex-start"
            spacing={gridSpacing}
            sx={{
              px: 3,
              py: 3,
              background: 'white',
              borderRadius: 3,
              boxShadow: 1
            }}
          >
            {saveClinicError && (
              <Grid item xs={12}>
                <Error
                  error={
                    !isEmpty(saveClinicError) && !isEmpty(saveClinicError.graphQLErrors)
                      ? saveClinicError.graphQLErrors[0]
                      : null
                  }
                />
              </Grid>
            )}

            {activeStep === stepsIndex.clinic && (
              <ClinicStep
                control={control}
                errors={errors}
                handleRetrieve={handleRetrieve}
              />
            )}

            {activeStep === stepsIndex.provider && (
              <ProviderStep
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                append={append}
                remove={remove}
                fields={fields}
                fieldsUser={fieldsUser}
                appendUser={appendUser}
                removeUser={removeUser}
                watch={watch}
              />
            )}

            {activeStep === stepsIndex.user && (
              <UserStep
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                appendUser={appendUser}
                removeUser={removeUser}
                fieldsUser={fieldsUser}
                existingUsers={existingUsers}
                setExistingUsers={setExistingUsers}
                addUserFromOtherClinicDialogVisible={addUserFromOtherClinicDialogVisible}
                setAddUserFromOtherClinicDialogVisible={setAddUserFromOtherClinicDialogVisible}
                watch={watch}
              />
            )}

            {activeStep === stepsIndex.review && (
              <Grid item xs={12}>
                <EnrollPsrReviewInfo
                  onEditClinic={handleOnEditClinic}
                  onEditProvider={handleOnEditProvider}
                  onEditUser={handleOnEditUser}
                  data={getValues()}
                  permissions={!isEmpty(data) ? data.user_permission_settings : []}
                  notificationSettings={
                    !isEmpty(parsedNotificationData) ? parsedNotificationData : []
                  }
                />
              </Grid>
            )}
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <MyButton
              color="primary"
              variant="outlined"
              disabled={activeStep === 0 || saveClinicLoading}
              onClick={handleBack}
              fullWidth={false}
            >
              Back
            </MyButton>

            {activeStep < steps.length - 1 && !showReviewButton && (
              <MyButton
                onClick={handleNext}
                disabled={
                  !isValid ||
                  saveClinicLoading ||
                  hasEmptyClinicField(dirtyFields, getValues()) ||
                  (!isEmpty(getValues('users')) && hasEmptyUserField(dirtyFields, getValues())) ||
                  (!isEmpty(getValues('providers')) && hasEmptyProviderField(dirtyFields, getValues()))
                }
                variant="contained"
                fullWidth={false}
              >
                Next
              </MyButton>
            )}

            {showReviewButton && (
              <MyButton
                onClick={() => setActiveStep(stepsIndex.review)}
                disabled={
                  !isValid ||
                  saveClinicLoading ||
                  hasEmptyClinicField(dirtyFields, getValues()) ||
                  (!isEmpty(getValues('users')) && hasEmptyUserField(dirtyFields, getValues())) ||
                  (!isEmpty(getValues('providers')) && hasEmptyProviderField(dirtyFields, getValues()))
                }
                variant="contained"
                color="secondary"
                fullWidth={false}
              >
                Review
              </MyButton>
            )}

            {activeStep === steps.length - 1 && (
              <MyButton
                type="submit"
                variant="contained"
                loading={saveClinicLoading}
                fullWidth={false}
              >
                Submit
              </MyButton>
            )}
          </Stack>
        </Stack>
      </form>
    </PageContent>
  );
}
